import { useEffect, useMemo, useState } from "react";
import { useForm, FormProvider, useWatch } from "react-hook-form";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { formConfig } from "./formConfig";
import { formSchema, buildDefaultValues, computeSectionStatus, FormValues } from "./schema";
import { generateExampleData } from "./exampleData";
import FieldRenderer from "./components/FieldRenderer";
import Tabs from "./components/Tabs";
import DraftsPanel from "./components/DraftsPanel";
import { getUser, isAdmin, ClientPrincipal, signInForCurrentPage } from "./auth/user";
import { getDraft } from "./api/drafts";
import { useAutoSave } from "./hooks/useAutoSave";

function firstVisibleIndex(): number {
  const i = formConfig.findIndex((s) => !s.hidden);
  return i < 0 ? 0 : i;
}
function adjacentVisibleIndex(from: number, dir: -1 | 1): number {
  let i = from + dir;
  while (i >= 0 && i < formConfig.length) {
    if (!formConfig[i].hidden) return i;
    i += dir;
  }
  return from;
}

function FormBody({
  draftId,
  setDraftId,
  setStatus,
  onSaved,
  activeTab,
  setActiveTab,
  submitAttempted,
  visited,
  authenticated
}: {
  draftId: string | null;
  setDraftId: (id: string) => void;
  setStatus: (s: string) => void;
  onSaved: () => void;
  activeTab: number;
  setActiveTab: (i: number) => void;
  submitAttempted: boolean;
  visited: boolean[];
  authenticated: boolean;
}) {
  useAutoSave(draftId, setDraftId, setStatus, onSaved, authenticated);
  const values = useWatch();
  const sectionResults = useMemo(
    () => computeSectionStatus(values as Record<string, unknown>, submitAttempted, visited),
    [values, submitAttempted, visited]
  );
  const statuses = sectionResults.map((r) => r.status);
  const section = formConfig[activeTab];
  return (
    <>
      <Tabs active={activeTab} onChange={setActiveTab} statuses={statuses} />
      <section>
        <div className="section-header">{section.title}</div>
        {section.fields.map((f) => (
          <FieldRenderer key={f.id} field={f} />
        ))}
      </section>
      <div className="tab-nav">
        <button
          type="button"
          disabled={adjacentVisibleIndex(activeTab, -1) === activeTab}
          onClick={() => setActiveTab(adjacentVisibleIndex(activeTab, -1))}
        >← Previous</button>
        {adjacentVisibleIndex(activeTab, 1) !== activeTab ? (
          <button type="button" onClick={() => setActiveTab(adjacentVisibleIndex(activeTab, 1))}>Next →</button>
        ) : (
          <span />
        )}
      </div>
    </>
  );
}

export default function App() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftQuery = searchParams.get("draft");
  const [user, setUser] = useState<ClientPrincipal | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [missingFields, setMissingFields] = useState<{ sectionTitle: string; question: string }[]>([]);
  const [draftId, setDraftId] = useState<string | null>(draftQuery);
  const [autoSaveStatus, setAutoSaveStatus] = useState<string>("");
  const [activeTab, setActiveTab] = useState(firstVisibleIndex);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [visited, setVisited] = useState<boolean[]>(() => formConfig.map((_, i) => i === firstVisibleIndex()));

  const setActiveTabAndVisit = (i: number) => {
    setActiveTab(i);
    setVisited((v) => {
      if (v[i]) return v;
      const next = v.slice();
      next[i] = true;
      return next;
    });
  };

  const methods = useForm<FormValues>({
    defaultValues: buildDefaultValues() as FormValues,
    mode: "onTouched"
  });

  useEffect(() => {
    getUser().then((u) => {
      if (!u) {
        signInForCurrentPage();
        return;
      }
      setUser(u);
      setAuthChecked(true);
    });
  }, []);

  useEffect(() => {
    setDraftId(draftQuery);
    setActiveTab(firstVisibleIndex());
    setVisited(formConfig.map((_, i) => i === firstVisibleIndex()));
    setSubmitAttempted(false);
    setMissingFields([]);
    setSubmitError(null);
    if (draftQuery && user) {
      setLoading(true);
      getDraft(draftQuery)
        .then((d) => methods.reset({ ...(buildDefaultValues() as FormValues), ...(d.data as FormValues) }))
        .catch(() => setSubmitError("Failed to load draft"))
        .finally(() => setLoading(false));
    } else {
      methods.reset(buildDefaultValues() as FormValues);
    }
  }, [draftQuery]);

  const onSubmit = async (values: FormValues) => {
    setSubmitError(null);
    setMissingFields([]);
    setSubmitAttempted(true);
    const parsed = formSchema.safeParse(values);
    if (!parsed.success) {
      const results = computeSectionStatus(values as Record<string, unknown>, true);
      const missing = results.flatMap((r) => r.missing);
      setMissingFields(missing);
      setSubmitError(`Please complete ${missing.length} required field${missing.length === 1 ? "" : "s"} before submitting.`);
      const firstBadIndex = results.findIndex((r) => r.status === "error");
      if (firstBadIndex >= 0) setActiveTabAndVisit(firstBadIndex);
      return;
    }
    if (!user) {
      signInForCurrentPage();
      return;
    }
    try {
      const res = await fetch("/api/submissions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: values, draftId })
      });
      if (!res.ok) throw new Error((await res.text()) || `Submission failed (${res.status})`);
      setRefreshKey((k) => k + 1);
      navigate("/thanks");
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : "Submission failed");
    }
  };

  if (!authChecked) {
    return <div className="loading-screen"><p>Loading…</p></div>;
  }

  return (
    <>
      <div className="layout">
        <main className="container">
          <div className="page-header">
            <h1>Precon Setup</h1>
            <span className="page-header-actions">
              {isAdmin(user) && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    methods.reset({ ...(buildDefaultValues() as FormValues), ...(generateExampleData() as FormValues) });
                  }}
                  title="Replace current form values with sample data"
                >
                  Fill example data
                </button>
              )}
              {isAdmin(user) && (
                <Link to="/admin" className="btn-secondary btn-admin-inline">⚙ Admin</Link>
              )}
              {user ? (
                <span className="user-info">
                  {user.displayName ?? user.userDetails} · <a href="/logout">Sign out</a>
                </span>
              ) : (
                <button type="button" className="btn-secondary" onClick={signInForCurrentPage}>Sign in to save</button>
              )}
            </span>
          </div>
          <div className="autosave-status">{autoSaveStatus}</div>
          {loading ? (
            <p>Loading draft…</p>
          ) : (
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit)} noValidate autoComplete="off">
                <div className="screen-only">
                  <FormBody
                    draftId={draftId}
                    setDraftId={setDraftId}
                    setStatus={setAutoSaveStatus}
                    onSaved={() => setRefreshKey((k) => k + 1)}
                    activeTab={activeTab}
                    setActiveTab={setActiveTabAndVisit}
                    submitAttempted={submitAttempted}
                    visited={visited}
                    authenticated={Boolean(user)}
                  />
                </div>
                <div className="print-only">
                  {formConfig.filter((sec) => !sec.hidden).map((sec) => (
                    <section key={sec.title} className="print-section">
                      <div className="section-header">{sec.title}</div>
                      {sec.fields.map((f) => (
                        <FieldRenderer key={f.id} field={f} />
                      ))}
                    </section>
                  ))}
                </div>
                {submitError && (
                  <div className="banner-error">
                    <div>{submitError}</div>
                    {missingFields.length > 0 && (
                      <ul className="missing-list">
                        {missingFields.map((m, i) => (
                          <li key={i}><strong>{m.sectionTitle}:</strong> {m.question}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                <div className="actions">
                  <button type="submit" disabled={methods.formState.isSubmitting}>
                    {methods.formState.isSubmitting ? "Submitting…" : "Submit"}
                  </button>
                  <span className="hint">Changes auto-save. You can leave and resume from the panel on the right.</span>
                </div>
              </form>
            </FormProvider>
          )}
        </main>
        {user && <DraftsPanel currentDraftId={draftId} onRefreshKey={refreshKey} />}
      </div>
      <footer className="app-footer">v0.4.1</footer>
    </>
  );
}
